import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalizeAssetData } from '../util/data';
import * as log from '../util/log';
import { storableError } from '../util/errors';

// ================ Helpers ================ //

const isValidPath = p => typeof p === 'string' && p.trim().length > 0;

// Pick paths from entries of appCdnAssets config (in configDefault.js)
const pickHostedConfigPaths = (assetEntries, excludeAssetNames) => {
  return assetEntries.reduce((pickedPaths, [name, path]) => {
    if (excludeAssetNames.includes(name)) return pickedPaths;
    if (!isValidPath(path)) return pickedPaths;
    return [...pickedPaths, path.trim()];
  }, []);
};

const getFirstAssetData = response => response?.data?.data?.[0]?.attributes?.data;
const getMultiAssetData = response => response?.data?.data || [];
const getMultiAssetIncluded = response => response?.data?.included || [];
const findJSONAsset = (assets, absolutePath) =>
  assets.find(a => a.type === 'jsonAsset' && a.attributes.assetPath === absolutePath);
const getAbsolutePath = path => (path && path.charAt(0) !== '/' ? `/${path}` : path);

const getGoogleAnalyticsId = (configAssets, path) => {
  if (!configAssets || !isValidPath(path)) return null;
  const configAssetsData = getMultiAssetData(configAssets);
  const jsonAsset = findJSONAsset(configAssetsData, getAbsolutePath(path.trim()));
  const { enabled, measurementId } = jsonAsset?.attributes?.data?.googleAnalytics || {};
  return enabled ? measurementId : null;
};

// ================ Async Thunks ================ //

// --- App-wide assets (translations, footer, config jsons) --- //
const fetchAppAssetsPayloadCreator = (arg, thunkAPI) => {
  const { assets, version } = arg || {};
  const { extra: sdk, rejectWithValue } = thunkAPI;

  // ✅ PURE MVP: if no app assets configured, do nothing.
  const assetEntriesRaw = assets && typeof assets === 'object' ? Object.entries(assets) : [];
  const assetEntries = assetEntriesRaw.filter(([_, path]) => isValidPath(path));
  if (!assetEntries.length) {
    return Promise.resolve({ assets: {}, version: null, googleAnalyticsId: null });
  }

  const translationsPath = isValidPath(assets?.translations) ? assets.translations.trim() : null;
  const footerPath = isValidPath(assets?.footer) ? assets.footer.trim() : null;

  const nonConfigAssets = ['translations', 'footer'];
  const configPaths = pickHostedConfigPaths(assetEntriesRaw, nonConfigAssets);

  // If there is literally nothing to fetch, bail out.
  const anyFetchable =
    isValidPath(translationsPath) || isValidPath(footerPath) || (configPaths && configPaths.length);

  if (!anyFetchable) {
    return Promise.resolve({ assets: {}, version: null, googleAnalyticsId: null });
  }

  // If version is given fetch assets by the version, otherwise use "latest" alias.
  const fetchAssets = paths => {
    const cleaned = Array.isArray(paths) ? paths.filter(isValidPath).map(p => p.trim()) : [];
    if (!cleaned.length) {
      // Never call sdk with empty paths.
      return Promise.resolve({ data: { data: [], included: [], meta: {} } });
    }
    return version
      ? sdk.assetsByVersion({ paths: cleaned, version })
      : sdk.assetsByAlias({ paths: cleaned, alias: 'latest' });
  };

  const translationFetch = translationsPath ? fetchAssets([translationsPath]) : Promise.resolve(null);
  const footerFetch = footerPath ? fetchAssets([footerPath]) : Promise.resolve(null);
  const configFetch = configPaths.length > 0 ? fetchAssets(configPaths) : Promise.resolve(null);

  return Promise.all([translationFetch, footerFetch, configFetch])
    .then(([translationAsset, footerAsset, configAssets]) => {
      const getVersionHash = response => response?.data?.meta?.version;
      const versionInTranslationsCall = getVersionHash(translationAsset);
      const versionInFooterCall = getVersionHash(footerAsset);
      const versionInConfigsCall = getVersionHash(configAssets);

      if (
        !version &&
        versionInTranslationsCall &&
        versionInFooterCall &&
        versionInConfigsCall &&
        !(
          versionInTranslationsCall === versionInFooterCall &&
          versionInFooterCall === versionInConfigsCall
        )
      ) {
        // eslint-disable-next-line no-console
        console.warn("Asset versions between calls don't match.");
      }

      const googleAnalyticsId = getGoogleAnalyticsId(configAssets, assets?.analytics);

      const fetchedConfigAssets = getMultiAssetData(configAssets);
      const included = getMultiAssetIncluded(configAssets);

      const collectedAssets = assetEntriesRaw.reduce((acc, [name, path]) => {
        if (!isValidPath(path)) return acc;

        const cleanedPath = path.trim();

        if (nonConfigAssets.includes(name)) {
          const assetResponse = name === 'translations' ? translationAsset : footerAsset;
          const data = getFirstAssetData(assetResponse);
          return { ...acc, [name]: { path: cleanedPath, data } };
        }

        const jsonAsset = findJSONAsset(fetchedConfigAssets, getAbsolutePath(cleanedPath));
        const data = denormalizeAssetData({
          data: jsonAsset?.attributes?.data,
          included,
        });

        return { ...acc, [name]: { path: cleanedPath, data } };
      }, {});

      const resolvedVersion =
        versionInTranslationsCall || versionInFooterCall || versionInConfigsCall || null;

      return {
        assets: collectedAssets,
        version: resolvedVersion,
        googleAnalyticsId,
      };
    })
    .catch(e => {
      log.error(e, 'app-asset-fetch-failed', { assets, version });
      return rejectWithValue(storableError(e));
    });
};

const fetchAppAssetsThunk = createAsyncThunk(
  'hostedAssets/fetchAppAssets',
  fetchAppAssetsPayloadCreator
);

// Wrapper functions to maintain the same API as the original thunks without Redux Toolkit.
export const fetchAppAssets = (assets, version) => (dispatch, getState, sdk) => {
  return dispatch(fetchAppAssetsThunk({ assets, version }))
    .unwrap()
    .then(result => result?.assets || {});
};

// --- Page assets (landing-page.json, cms pages, etc.) --- //
const fetchPageAssetsPayloadCreator = (arg, thunkAPI) => {
  const { assets, hasFallback } = arg || {};
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;

  // ✅ PURE MVP: if no page assets configured, do nothing.
  if (!assets || typeof assets !== 'object' || Object.keys(assets).length === 0) {
    return Promise.resolve({});
  }

  const version = getState()?.hostedAssets?.version;

  // ✅ PURE MVP: if app-wide assets weren't fetched (no version), skip page assets.
  if (typeof version === 'undefined' || version === null) {
    return Promise.resolve({});
  }

  const fetchAsset = assetPath => {
    if (!isValidPath(assetPath)) {
      return Promise.resolve({ data: { data: null, included: [] } });
    }
    const cleaned = assetPath.trim();
    return version
      ? sdk.assetByVersion({ path: cleaned, version })
      : sdk.assetByAlias({ path: cleaned, alias: 'latest' });
  };

  const assetEntries = Object.entries(assets).filter(([_, p]) => isValidPath(p));
  if (!assetEntries.length) {
    return Promise.resolve({});
  }

  const sdkAssets = assetEntries.map(([_, assetPath]) => fetchAsset(assetPath));

  return Promise.all(sdkAssets)
    .then(responses => {
      const hostedAssetsState = getState()?.hostedAssets;

      // Keep fixed assets always in store if present
      const { termsOfService, privacyPolicy, landingPage, ...rest } =
        hostedAssetsState?.pageAssetsData || {};
      const fixedPageAssets = { termsOfService, privacyPolicy, landingPage };

      const pickLatestPageAssetData = hostedAssetsState?.currentPageAssets?.reduce(
        (collected, pa) => {
          const cmsPageData = rest[pa];
          return cmsPageData ? { ...collected, [pa]: cmsPageData } : collected;
        },
        {}
      );

      const pageAssets = assetEntries.reduce((collectedAssets, assetEntry, i) => {
        const [name, path] = assetEntry;
        const assetData = denormalizeAssetData(responses[i].data);
        return { ...collectedAssets, [name]: { path: path.trim(), data: assetData } };
      }, { ...fixedPageAssets, ...(pickLatestPageAssetData || {}) });

      return pageAssets;
    })
    .catch(e => {
      if (hasFallback || (!hasFallback && e.status === 404)) {
        log.error(e, 'page-asset-fetch-failed', { assets, version });
      }
      return rejectWithValue(storableError(e));
    });
};

const fetchPageAssetsThunk = createAsyncThunk(
  'hostedAssets/fetchPageAssets',
  fetchPageAssetsPayloadCreator
);

// Wrapper functions to maintain the same API as the original thunks without Redux Toolkit.
export const fetchPageAssets = (assets, hasFallback) => (dispatch, getState, sdk) => {
  return dispatch(fetchPageAssetsThunk({ assets, hasFallback })).unwrap();
};

// ================ Slice ================ //

const hostedAssetsSlice = createSlice({
  name: 'hostedAssets',
  initialState: {
    appAssets: {},
    pageAssetsData: null,
    currentPageAssets: [],
    version: null,
    googleAnalyticsId: null,
    inProgress: false,
    error: null,
  },
  reducers: {
    setCurrentPageAssets: (state, action) => {
      state.currentPageAssets = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // fetchAppAssets cases
      .addCase(fetchAppAssetsThunk.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchAppAssetsThunk.fulfilled, (state, action) => {
        state.appAssets = action.payload?.assets || {};
        state.version = state.version || action.payload?.version || null;
        state.googleAnalyticsId = action.payload?.googleAnalyticsId || null;
        state.inProgress = false;
      })
      .addCase(fetchAppAssetsThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      })
      // fetchPageAssets cases
      .addCase(fetchPageAssetsThunk.pending, (state, action) => {
        state.currentPageAssets = action.meta.arg.assets ? Object.keys(action.meta.arg.assets) : [];
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchPageAssetsThunk.fulfilled, (state, action) => {
        state.pageAssetsData = action.payload || {};
        state.inProgress = false;
      })
      .addCase(fetchPageAssetsThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentPageAssets } = hostedAssetsSlice.actions;
export default hostedAssetsSlice.reducer;